import type { Transaction } from '@api/hooks/types/getTransactions';
import { Direction } from '@api/hooks/types/getTransactions';
import type { ColumnProps } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { OperationStatus } from 'types/operations';

import { OperationCellMobile } from '../components/OperationCellMobile';

const mobileColumns: ColumnProps[] = [
	{ key: 'mobileOperation', label: '', align: 'left' },
];

const desktopRenders: Record<string, (row: Transaction) => ReactNode> = {
	beneficiary: ({ beneficiary, status }) => (
		<div className="flex flex-col">
			<div className="flex items-center gap-space-4">
				<span className="text-sm-bold text-neutral-950">{beneficiary.name}</span>
				{status === OperationStatus.FAILED && (
					<span className="text-xs-bold text-status-error-700 bg-brand-primary-50 rounded-full px-space-8 h-5 inline-flex items-center shrink-0">
						Failed
					</span>
				)}
			</div>
			<span className="text-sm-medium text-neutral-600">
				{beneficiary.account_number}
			</span>
		</div>
	),
	amount: ({ amount, direction }) => (
		<span
			className={clsx(
				'text-sm-bold',
				direction === Direction.INWARD ? 'text-brand-secondary-700' : 'text-neutral-950',
			)}
		>
			{direction === Direction.INWARD ? '+' : '-'}
			{Math.abs(amount).toLocaleString('lt-LT', { minimumFractionDigits: 2 })}
		</span>
	),
};

const mobileRenders = { ...desktopRenders, mobileOperation: OperationCellMobile };

export const useOperationsTable = () => {
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const { t } = useTranslation();

	const desktopColumns: ColumnProps[] = [
		{
			label: t('listOfOperations.table.beneficiary'),
			key: 'beneficiary',
			align: 'left',
			minWidth: 260,
		},
		{
			label: t('listOfOperations.table.description'),
			key: 'payment_details',
			align: 'left',
			minWidth: 200,
		},
		{
			label: t('listOfOperations.table.date'),
			key: 'execution_date',
			align: 'left',
			minWidth: 130,
		},
		{
			label: t('listOfOperations.table.amount'),
			key: 'amount',
			align: 'right',
			minWidth: 160,
		},
	];

	return {
		columns: isMobile ? mobileColumns : desktopColumns,
		renders: isMobile ? mobileRenders : desktopRenders,
		showHeader: !isMobile,
	};
};
